import { inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';

import { runtimeSelectors } from '@blaze-runtime/engine-data-access';
import {
  ComparisonOperator,
  LogicalPredicateNode,
  LogicalType,
  PredicateType,
  SimplePredicateNode,
} from '@blaze-shared/api-contracts-domain';
import { syncSelect } from '@blaze-shared/platform-data-access';
import { DatasetRowsService } from '@blaze-shared/schema-data-access';
import { BlazeSchema, DatasetRow, findColumn } from '@blaze-shared/schema-domain';
import { isPivotNode } from '@blaze-shared/workflow-domain';

@Injectable({ providedIn: 'root' })
export class TableInlineEditorUtilsService {
  private datasetRowsService = inject(DatasetRowsService);
  private store = inject(Store);

  closeInlineEditor = new Subject();

  updateInlineRowValue(
    schema: BlazeSchema | undefined,
    workflowId: string | undefined,
    datasetId: string,
    value: any,
    columnName: string,
    id: string,
    idColumnName: string,
    dataRow: Record<string, any>,
  ): Observable<DatasetRow | undefined> {
    /**
     * If the schema is after pivot, we need to create other conditions to update the row
     */
    const pivotNode =
      workflowId &&
      syncSelect(this.store, runtimeSelectors.selectFirstNodeInWorkflow(workflowId, isPivotNode));

    if (!pivotNode) {
      return this.updateInlineRow(datasetId, value, columnName, id, idColumnName);
    }
    const groupByColumns: [string, any][] = pivotNode.groupByColumns.map((column) => {
      const colName = findColumn(schema?.columns, column)?.name || column;
      return [column, dataRow[colName]];
    });
    const pivotColumn: [string, any] = [pivotNode.pivotColumn, columnName];
    return this.updateInlineRowAfterPivot(
      datasetId,
      value,
      pivotNode.aggregateFunction.column ?? '',
      groupByColumns,
      pivotColumn,
    );
  }

  private updateInlineRow(
    datasetId: string,
    value: any,
    columnName: string,
    id: string,
    idColumnName: string,
  ): Observable<DatasetRow | undefined> {
    return this.datasetRowsService.update(
      datasetId,
      {
        value: { [columnName]: value },
        queryNode: {
          nodeType: PredicateType.Logical,
          logicalType: LogicalType.And,
          children: [
            {
              names: [idColumnName],
              op: ComparisonOperator.Eq,
              value: id,
              nodeType: PredicateType.Comparison,
            },
          ],
        },
      },
      {
        showThemedNotification: true,
        omitCatchError: true,
      },
    );
  }

  private updateInlineRowAfterPivot(
    datasetId: string,
    value: any,
    aggregateColumn: string,
    groupByColumns: [string, any][],
    pivotColumn: [string, any],
  ): Observable<DatasetRow | undefined> {
    const groupByQueryNodeChildren: (LogicalPredicateNode | SimplePredicateNode)[] =
      groupByColumns.map(([name, value]) => ({
        nodeType: PredicateType.Comparison,
        names: [name],
        op: ComparisonOperator.Eq,
        value,
      }));
    const aggregateQueryNodeChildren: LogicalPredicateNode | SimplePredicateNode = {
      nodeType: PredicateType.Comparison,
      names: [pivotColumn[0]],
      op: ComparisonOperator.Eq,
      value: pivotColumn[1],
    };
    const queryNodeChildren: (LogicalPredicateNode | SimplePredicateNode)[] = [
      ...groupByQueryNodeChildren,
      aggregateQueryNodeChildren,
    ];

    return this.datasetRowsService.update(
      datasetId,
      {
        value: { [aggregateColumn]: value },
        queryNode: {
          nodeType: PredicateType.Logical,
          logicalType: LogicalType.And,
          children: queryNodeChildren,
        },
      },
      {
        showThemedNotification: true,
        omitCatchError: true,
      },
    );
  }
}
